import { GeniePlayBridge, PadControl } from "./GeniePlayBridge";

const sleep = (milliseconds: number) =>
  new Promise<void>((resolve) => setTimeout(resolve, milliseconds));

const PILOT_STATE_KEY = "geniewoman.mm2.pilot-state.v1";

export type PilotAction =
  | { type: "hold"; control: PadControl; milliseconds: number }
  | { type: "tap"; control: PadControl; milliseconds?: number }
  | { type: "wait"; milliseconds: number };

export type PersistentPilotState = {
  completedRuns: number;
  lastRoutine: string;
  lastStartedAt: string;
  lastCompletedAt: string;
  interrupted: boolean;
  currentActionIndex: number;
  plannedActionCount: number;
  lastCheckpointAt: string;
  routineSignature: string;
  lastActionDescription: string;
  runtimeIdentity: string;
};

export type PilotResumeCandidate = {
  routineName: string;
  routineSignature: string;
  completedActionCount: number;
  plannedActionCount: number;
  lastActionDescription: string;
  checkpointAt: string;
  runtimeIdentity: string;
};

export type PilotResumePreview = PilotResumeCandidate & {
  remainingActionCount: number;
  nextActionDescription: string;
  checkpointFingerprint: string;
};

const emptyPilotState = (): PersistentPilotState => ({
  completedRuns: 0,
  lastRoutine: "",
  lastStartedAt: "",
  lastCompletedAt: "",
  interrupted: false,
  currentActionIndex: 0,
  plannedActionCount: 0,
  lastCheckpointAt: "",
  routineSignature: "",
  lastActionDescription: "",
  runtimeIdentity: "",
});

const loadPilotState = (): PersistentPilotState => {
  try {
    const raw = globalThis.localStorage?.getItem(PILOT_STATE_KEY);
    if (!raw) return emptyPilotState();
    const parsed = JSON.parse(raw) as Partial<PersistentPilotState>;
    return {
      completedRuns: Number.isFinite(parsed.completedRuns) ? Number(parsed.completedRuns) : 0,
      lastRoutine: typeof parsed.lastRoutine === "string" ? parsed.lastRoutine : "",
      lastStartedAt: typeof parsed.lastStartedAt === "string" ? parsed.lastStartedAt : "",
      lastCompletedAt: typeof parsed.lastCompletedAt === "string" ? parsed.lastCompletedAt : "",
      interrupted: parsed.interrupted === true,
      currentActionIndex: Number.isFinite(parsed.currentActionIndex)
        ? Math.max(0, Number(parsed.currentActionIndex))
        : 0,
      plannedActionCount: Number.isFinite(parsed.plannedActionCount)
        ? Math.max(0, Number(parsed.plannedActionCount))
        : 0,
      lastCheckpointAt:
        typeof parsed.lastCheckpointAt === "string" ? parsed.lastCheckpointAt : "",
      routineSignature:
        typeof parsed.routineSignature === "string" ? parsed.routineSignature : "",
      lastActionDescription:
        typeof parsed.lastActionDescription === "string" ? parsed.lastActionDescription : "",
      runtimeIdentity:
        typeof parsed.runtimeIdentity === "string" ? parsed.runtimeIdentity : "",
    };
  } catch {
    return emptyPilotState();
  }
};

const savePilotState = (state: PersistentPilotState): void => {
  try {
    globalThis.localStorage?.setItem(PILOT_STATE_KEY, JSON.stringify(state));
  } catch {
    // Continuity metadata is best-effort. Loss of storage must never block control.
  }
};

const describeAction = (action: PilotAction): string => {
  switch (action.type) {
    case "hold":
      return `hold:${action.control}:${action.milliseconds}`;
    case "tap":
      return `tap:${action.control}:${action.milliseconds ?? 80}`;
    case "wait":
      return `wait:${action.milliseconds}`;
  }
};

const signatureForActions = (routineName: string, actions: PilotAction[]): string =>
  `${routineName}|${actions.map(describeAction).join("|")}`;

const fingerprintForCandidate = (candidate: PilotResumeCandidate): string =>
  [
    candidate.routineSignature,
    candidate.completedActionCount,
    candidate.plannedActionCount,
    candidate.checkpointAt,
    candidate.runtimeIdentity,
  ].join("|");

const squarePatrolActions = (sideMilliseconds: number): PilotAction[] => {
  const actions: PilotAction[] = [];
  for (let side = 0; side < 4; side += 1) {
    actions.push(
      { type: "hold", control: "LEFT_STICK_UP", milliseconds: sideMilliseconds },
      { type: "wait", milliseconds: 120 },
      { type: "hold", control: "RIGHT_STICK_RIGHT", milliseconds: 340 },
      { type: "wait", milliseconds: 120 },
    );
  }
  return actions;
};

/**
 * Minimal external controller for the MM2 avatar.
 *
 * This is intentionally deterministic. The first test needs to prove that an
 * external agent can inhabit the existing player body, not that random input
 * can make a character stumble around.
 */
export class AutonomousPilot {
  private running = false;
  private persistent = loadPilotState();

  constructor(private readonly bridge: GeniePlayBridge) {
    if (this.persistent.interrupted) {
      savePilotState(this.persistent);
    }
  }

  isRunning(): boolean {
    return this.running;
  }

  persistentState(): PersistentPilotState {
    return { ...this.persistent };
  }

  private runtimeIdentity(): string {
    return `${this.bridge.executableName()}|ee-ram:${this.bridge.eeRamSize()}`;
  }

  resumeCandidate(): PilotResumeCandidate | null {
    if (!this.persistent.interrupted || !this.persistent.routineSignature) {
      return null;
    }
    return {
      routineName: this.persistent.lastRoutine,
      routineSignature: this.persistent.routineSignature,
      completedActionCount: this.persistent.currentActionIndex,
      plannedActionCount: this.persistent.plannedActionCount,
      lastActionDescription: this.persistent.lastActionDescription,
      checkpointAt: this.persistent.lastCheckpointAt,
      runtimeIdentity: this.persistent.runtimeIdentity,
    };
  }

  /**
   * Read-only inspection for an interrupted deterministic routine. This lets a
   * higher-level decision layer see exactly what would happen next before it
   * deliberately calls resume(). No controller input is issued here.
   */
  resumePreview(actions: PilotAction[], routineName = "custom-sequence"): PilotResumePreview | null {
    const candidate = this.resumeCandidate();
    if (!candidate) return null;

    const requestedSignature = signatureForActions(routineName, actions);
    if (requestedSignature !== candidate.routineSignature) return null;
    if (candidate.plannedActionCount !== actions.length) return null;
    if (
      candidate.completedActionCount < 0 ||
      candidate.completedActionCount > candidate.plannedActionCount
    ) {
      return null;
    }
    if (!candidate.runtimeIdentity || candidate.runtimeIdentity !== this.runtimeIdentity()) {
      return null;
    }

    const nextAction = actions[candidate.completedActionCount];
    return {
      ...candidate,
      remainingActionCount: Math.max(0, actions.length - candidate.completedActionCount),
      nextActionDescription: nextAction ? describeAction(nextAction) : "",
      checkpointFingerprint: fingerprintForCandidate(candidate),
    };
  }

  stop(): void {
    this.running = false;
  }

  /**
   * Deliberately abandon an interrupted routine after inspecting it. The
   * fingerprint requirement prevents a stale decision from clearing a newer
   * checkpoint that appeared between inspection and discard.
   */
  discardInterruptedRoutine(expectedCheckpointFingerprint: string): boolean {
    if (this.running) throw new Error("Cannot discard an MM2 pilot routine while it is running");

    const candidate = this.resumeCandidate();
    if (!candidate) return false;
    if (expectedCheckpointFingerprint !== fingerprintForCandidate(candidate)) {
      throw new Error("Discard refused: persisted checkpoint changed after inspection");
    }

    this.persistent = {
      ...this.persistent,
      interrupted: false,
      lastCheckpointAt: new Date().toISOString(),
      lastActionDescription: "discarded",
    };
    savePilotState(this.persistent);
    return true;
  }

  private beginRoutine(
    name: string,
    plannedActionCount: number,
    routineSignature: string,
  ): void {
    this.persistent = {
      ...this.persistent,
      lastRoutine: name,
      lastStartedAt: new Date().toISOString(),
      interrupted: true,
      currentActionIndex: 0,
      plannedActionCount,
      lastCheckpointAt: "",
      routineSignature,
      lastActionDescription: "",
      runtimeIdentity: this.runtimeIdentity(),
    };
    savePilotState(this.persistent);
  }

  private checkpoint(completedActionCount: number, lastActionDescription: string): void {
    this.persistent = {
      ...this.persistent,
      currentActionIndex: Math.max(0, completedActionCount),
      lastCheckpointAt: new Date().toISOString(),
      lastActionDescription,
    };
    savePilotState(this.persistent);
  }

  private finishRoutine(): void {
    this.persistent = {
      ...this.persistent,
      completedRuns: this.persistent.completedRuns + 1,
      lastCompletedAt: new Date().toISOString(),
      interrupted: false,
      currentActionIndex: this.persistent.plannedActionCount,
      lastCheckpointAt: new Date().toISOString(),
    };
    savePilotState(this.persistent);
  }

  private async executeAction(action: PilotAction): Promise<void> {
    switch (action.type) {
      case "hold":
        await this.bridge.hold(action.control, action.milliseconds);
        break;
      case "tap":
        await this.bridge.tap(action.control, action.milliseconds ?? 80);
        break;
      case "wait":
        await sleep(action.milliseconds);
        break;
    }
  }

  async run(actions: PilotAction[], routineName = "custom-sequence"): Promise<void> {
    if (this.running) throw new Error("Pilot is already running");

    this.running = true;
    this.beginRoutine(routineName, actions.length, signatureForActions(routineName, actions));
    let completed = false;
    try {
      for (let actionIndex = 0; actionIndex < actions.length; actionIndex += 1) {
        if (!this.running) break;
        const action = actions[actionIndex];
        await this.executeAction(action);
        this.checkpoint(actionIndex + 1, describeAction(action));
      }
      completed = this.running;
    } finally {
      this.running = false;
      if (completed) this.finishRoutine();
    }
  }

  async resume(
    actions: PilotAction[],
    routineName = "custom-sequence",
    expectedCheckpointFingerprint?: string,
  ): Promise<void> {
    if (this.running) throw new Error("Pilot is already running");

    const candidate = this.resumeCandidate();
    if (!candidate) throw new Error("No interrupted MM2 pilot routine is available to resume");

    const requestedSignature = signatureForActions(routineName, actions);
    if (requestedSignature !== candidate.routineSignature) {
      throw new Error("Resume refused: routine signature does not match persisted checkpoint");
    }
    if (candidate.plannedActionCount !== actions.length) {
      throw new Error("Resume refused: persisted action count does not match routine definition");
    }
    if (
      candidate.completedActionCount < 0 ||
      candidate.completedActionCount > candidate.plannedActionCount
    ) {
      throw new Error("Resume refused: persisted checkpoint is outside the routine bounds");
    }
    if (!candidate.runtimeIdentity) {
      throw new Error("Resume refused: checkpoint predates MM2 runtime identity binding");
    }
    if (candidate.runtimeIdentity !== this.runtimeIdentity()) {
      throw new Error("Resume refused: emulator runtime identity changed since checkpoint");
    }
    if (
      expectedCheckpointFingerprint !== undefined &&
      expectedCheckpointFingerprint !== fingerprintForCandidate(candidate)
    ) {
      throw new Error("Resume refused: persisted checkpoint changed after inspection");
    }

    this.running = true;
    let completed = false;
    try {
      for (
        let actionIndex = candidate.completedActionCount;
        actionIndex < actions.length;
        actionIndex += 1
      ) {
        if (!this.running) break;
        const action = actions[actionIndex];
        await this.executeAction(action);
        this.checkpoint(actionIndex + 1, describeAction(action));
      }
      completed = this.running;
    } finally {
      this.running = false;
      if (completed) this.finishRoutine();
    }
  }

  async smokeTest(): Promise<void> {
    await this.run([
      { type: "wait", milliseconds: 750 },
      { type: "hold", control: "LEFT_STICK_UP", milliseconds: 900 },
      { type: "wait", milliseconds: 300 },
      { type: "hold", control: "RIGHT_STICK_RIGHT", milliseconds: 250 },
      { type: "wait", milliseconds: 250 },
      { type: "hold", control: "LEFT_STICK_UP", milliseconds: 600 },
    ], "smoke-test");
  }

  patrolSquareResumePreview(sideMilliseconds = 650): PilotResumePreview | null {
    return this.resumePreview(squarePatrolActions(sideMilliseconds), "square-patrol");
  }

  async resumePatrolSquare(
    sideMilliseconds = 650,
    expectedCheckpointFingerprint?: string,
  ): Promise<void> {
    await this.resume(
      squarePatrolActions(sideMilliseconds),
      "square-patrol",
      expectedCheckpointFingerprint,
    );
  }

  async patrolSquare(sideMilliseconds = 650): Promise<void> {
    await this.run(squarePatrolActions(sideMilliseconds), "square-patrol");
  }
}

export function makeAutonomousPilot(playModule: any) {
  const bridge = new GeniePlayBridge(playModule);
  const pilot = new AutonomousPilot(bridge);
  return { bridge, pilot };
}
